'use client';

import { API_ROUTES, createQueryKey, HTTP_METHODS, useApiCall } from '@shared/api-utils';
import { useQueries } from '@tanstack/react-query';
import type { LotteryName } from '../../lottery-purchases/types';
import type { Drawing, DrawingsResponse } from '../../lottery-results/types';

type ResultsCarouselData = {
  displayData: Drawing[];
  hasResults: boolean;
  isLoadingResults: boolean;
};

/**
 * Custom React hook to fetch and aggregate recent lottery results for a given list of lotteries.
 * @param lotteries - The list of lottery names to fetch results for.
 * @returns The sorted drawing data, a loading flag, and a "hasResults" flag.
 * @description For each lottery name provided:
 * - Initiates an API call to fetch the most recent drawing result (limit: 1).
 * - Aggregates, processes and sorts available drawing results by drawing date (most recent first).
 * - Indicates loading state while results are being fetched.
 */

function useResultsCarouselData(lotteries: LotteryName[]): ResultsCarouselData {
  const apiCall = useApiCall();
  const queries = useQueries({
    queries: lotteries.map(lotteryName => ({
      queryKey: createQueryKey(API_ROUTES.LOTTERIES.LOTTERY_RESULTS.replace(':lotteryName', lotteryName), { limit: 1 }),
      queryFn: (): Promise<DrawingsResponse | null> => apiCall<DrawingsResponse>(API_ROUTES.LOTTERIES.LOTTERY_RESULTS.replace(':lotteryName', lotteryName), HTTP_METHODS.GET, {}),
    })),
  });

  const fetchedData: Drawing[] = queries.flatMap(query => {
    const data = query.data;
    if (data?.[0]) {
      return [{ ...(data[0] as Drawing), extraGames: [] }];
    }
    return [];
  });

  const isLoadingResults = lotteries.length === 0 ? true : queries.some(query => query.isLoading);
  const hasResults = fetchedData.length > 0;

  const displayData = !isLoadingResults
    ? fetchedData
        .map(result => ({ ...result, extraGames: [] }))
        .sort((first: Drawing, second: Drawing) => {
          const firstDate = new Date(first.drawingDate).getTime();
          const secondDate = new Date(second.drawingDate).getTime();
          return secondDate - firstDate;
        })
    : [];

  return { displayData, hasResults, isLoadingResults };
}

export { useResultsCarouselData };
